
import { SOUND_FREQUENCIES } from '../constants';

class AudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private isMuted: boolean = false;
  private reverbNode: ConvolverNode | null = null;

  constructor() {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.ctx = new AudioContextClass();
        
        // Dynamic Compressor to glue sounds together and prevent clipping
        this.compressor = this.ctx.createDynamicsCompressor();
        this.compressor.threshold.value = -24;
        this.compressor.knee.value = 30;
        this.compressor.ratio.value = 12;
        this.compressor.attack.value = 0.003;
        this.compressor.release.value = 0.25;

        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.value = 0.6; // Headroom

        this.compressor.connect(this.masterGain);
        this.masterGain.connect(this.ctx.destination);
      }
    } catch (e) {
      console.warn("Web Audio API not supported");
    }
  }

  public init() {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  public toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  public playMergeSound(level: number) {
    if (this.isMuted || !this.ctx || !this.compressor) return;

    const t = this.ctx.currentTime;
    const baseFreq = SOUND_FREQUENCIES[level % SOUND_FREQUENCIES.length] || 440;
    
    const carrier = this.ctx.createOscillator();
    const carrierGain = this.ctx.createGain();
    const modulator = this.ctx.createOscillator();
    const modulatorGain = this.ctx.createGain();

    const ratio = 2.0; 
    carrier.frequency.setValueAtTime(baseFreq, t);
    carrier.type = 'sine';
    modulator.frequency.setValueAtTime(baseFreq * ratio, t);
    modulator.type = 'sine';
    modulatorGain.gain.setValueAtTime(1000, t); 
    modulatorGain.gain.exponentialRampToValueAtTime(0.1, t + 0.3);

    carrierGain.gain.setValueAtTime(0, t);
    carrierGain.gain.linearRampToValueAtTime(0.4, t + 0.02);
    carrierGain.gain.exponentialRampToValueAtTime(0.001, t + 1.5);

    modulator.connect(modulatorGain);
    modulatorGain.connect(carrier.frequency);
    carrier.connect(carrierGain);

    const delayL = this.ctx.createDelay();
    const delayR = this.ctx.createDelay();
    const delayGain = this.ctx.createGain();
    delayL.delayTime.value = 0.15;
    delayR.delayTime.value = 0.20;
    delayGain.gain.value = 0.2;

    carrierGain.connect(delayL);
    carrierGain.connect(delayR);
    delayL.connect(delayGain);
    delayR.connect(delayGain);
    carrierGain.connect(this.compressor);
    delayGain.connect(this.compressor);

    carrier.start(t);
    carrier.stop(t + 1.5);
    modulator.start(t);
    modulator.stop(t + 1.5);
  }

  public playDropSound() {
    if (this.isMuted || !this.ctx || !this.compressor) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.1);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(gain);
    gain.connect(this.compressor);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  public playOverheatSound() {
    if (this.isMuted || !this.ctx || !this.compressor) return;
    const t = this.ctx.currentTime;
    
    // Low Buzz Glitch
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(120, t);
    osc.frequency.linearRampToValueAtTime(40, t + 0.3);
    
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.2, t + 0.05);
    gain.gain.linearRampToValueAtTime(0, t + 0.3);

    osc.connect(gain);
    gain.connect(this.compressor);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  public playLevelUpSound() {
    if (this.isMuted || !this.ctx || !this.compressor) return;
    const t = this.ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.50];
    notes.forEach((freq, i) => {
        const osc = this.ctx!.createOscillator();
        const gain = this.ctx!.createGain();
        osc.frequency.setValueAtTime(freq, t + (i * 0.1));
        osc.type = 'triangle';
        gain.gain.setValueAtTime(0, t + (i * 0.1));
        gain.gain.linearRampToValueAtTime(0.2, t + (i * 0.1) + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, t + (i * 0.1) + 1.0);
        osc.connect(gain);
        gain.connect(this.compressor!);
        osc.start(t + (i * 0.1));
        osc.stop(t + (i * 0.1) + 1.0);
    });
  }

  public playExplosionSound() {
    if (this.isMuted || !this.ctx || !this.compressor) return;
    const t = this.ctx.currentTime;
    const duration = 1.2;
    const subOsc = this.ctx.createOscillator();
    const subGain = this.ctx.createGain();
    subOsc.type = 'triangle';
    subOsc.frequency.setValueAtTime(100, t);
    subOsc.frequency.exponentialRampToValueAtTime(20, t + 0.5);
    subGain.gain.setValueAtTime(0.8, t);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    subOsc.connect(subGain);
    subGain.connect(this.compressor);
    subOsc.start(t);
    subOsc.stop(t + 0.6);

    const bufferSize = this.ctx.sampleRate * duration;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1);
    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3000, t);
    filter.frequency.exponentialRampToValueAtTime(100, t + duration);
    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.6, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + duration);
    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this.compressor);
    noise.start(t);
  }
}

export const audioManager = new AudioEngine();
